import React from "react";
import {
    Grid,
    TextField,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Typography,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { formatToUIDateTimeLong } from "../utils/dateUtils";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { isSameDay } from "date-fns";


interface VitalsFormProps {
    todaysVitals: any;
    setTodaysVitals: React.Dispatch<React.SetStateAction<any>>;
    handleSaveVitals: () => Promise<void>;
    isSavingVitals: boolean;
    effectiveResidentId: string | null;
    editingVitalId: string | null;
    cancelEditVital: () => void;
    vitalsError: string | null;
    activeTab?: number;
    latestVital?: any;
    vitalsHistory?: any[];
    handleEditVital?: (vital: any) => void;
    bowelMovementOptions?: { id: string; valueName: string }[];
}

const VitalsForm: React.FC<VitalsFormProps> = ({
    todaysVitals,
    setTodaysVitals,
    handleSaveVitals,
    isSavingVitals,
    effectiveResidentId,
    editingVitalId,
    cancelEditVital,
    vitalsError,
    activeTab = 0,
    latestVital,
    vitalsHistory = [],
    handleEditVital,
    bowelMovementOptions = [],
}) => {
    if (activeTab === 0) {
        return (
            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Date & Time</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Weight</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Temp <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Pulse <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Blood Pressure</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Resp</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Blood Sugar</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>SpO2</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Pain</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>LOC</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Meal Portion</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Bowel Movement</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ minWidth: 220 }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DateTimePicker
                                        format="dd/MM/yyyy hh:mm a"
                                        value={todaysVitals.vitalDate}
                                        maxDate={new Date()}
                                        readOnly={true}
                                        minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                        onChange={(newValue) =>
                                            setTodaysVitals((prev: any) => ({
                                                ...prev,
                                                vitalDate: newValue,
                                            }))
                                        }
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: "small"
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.weight}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d{0,5}(\.\d{0,5})?$/.test(val)) {
                                            setTodaysVitals((prev: any) => ({ ...prev, weight: val }));
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.temperature}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d{0,5}(\.\d{0,5})?$/.test(val)) {
                                            setTodaysVitals((prev: any) => ({ ...prev, temperature: val }));
                                        }
                                    }}
                                    error={!!vitalsError && !todaysVitals.temperature}
                                    required
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.pulseRate}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d{0,5}$/.test(val)) {
                                            setTodaysVitals((prev: any) => ({ ...prev, pulseRate: val }));
                                        }
                                    }}
                                    error={!!vitalsError && !todaysVitals.pulseRate}
                                    required
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 130 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="120/80"
                                    value={todaysVitals.bloodPressure}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^[0-9/]*$/.test(val) && val.length <= 7) {
                                            setTodaysVitals((prev: any) => ({ ...prev, bloodPressure: val }));
                                        }
                                    }}
                                    error={todaysVitals.bloodPressure !== "" && !/^\d{2,3}\/\d{2,3}$/.test(todaysVitals.bloodPressure)}
                                    helperText={
                                        todaysVitals.bloodPressure !== "" && !/^\d{2,3}\/\d{2,3}$/.test(todaysVitals.bloodPressure)
                                            ? "Format: 120/80"
                                            : ""
                                    }
                                    sx={{
                                        "& .MuiInputBase-input::placeholder": {
                                            color: "#9CA3AF !important",
                                            WebkitTextFillColor: "#9CA3AF !important",
                                            opacity: "1 !important"
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.respirationRate}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d{0,5}$/.test(val)) {
                                            setTodaysVitals((prev: any) => ({ ...prev, respirationRate: val }));
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.bloodSugar}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d{0,5}(\.\d{0,5})?$/.test(val)) {
                                            setTodaysVitals((prev: any) => ({ ...prev, bloodSugar: val }));
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.oxygenSaturation}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d{0,5}(\.\d{0,5})?$/.test(val)) {
                                            setTodaysVitals((prev: any) => ({ ...prev, oxygenSaturation: val }));
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.painLevel}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d*$/.test(val) && !/^0\d/.test(val)) {
                                            if (val === "" || (Number(val) >= 0 && Number(val) <= 10)) {
                                                setTodaysVitals((prev: any) => ({ ...prev, painLevel: val }));
                                            }
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 120 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.consciousnessLevel}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^[a-zA-Z0-9\s]*$/.test(val) && val.length <= 5) {
                                            setTodaysVitals((prev: any) => ({ ...prev, consciousnessLevel: val }));
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={todaysVitals.mealPortionConsumed}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^\d*$/.test(val) && !/^0\d/.test(val)) {
                                            if (val === "" || (Number(val) >= 0 && Number(val) <= 100)) {
                                                setTodaysVitals((prev: any) => ({ ...prev, mealPortionConsumed: val }));
                                            }
                                        }
                                    }}
                                />
                            </TableCell>
                            <TableCell sx={{ minWidth: 140 }}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={todaysVitals.bowelMovement}
                                        displayEmpty
                                        onChange={(e) => setTodaysVitals((prev: any) => ({ ...prev, bowelMovement: e.target.value }))}
                                    >
                                        <MenuItem value="" disabled>Select</MenuItem>
                                        {bowelMovementOptions.map((opt) => (
                                            <MenuItem key={opt.id} value={opt.valueName}>
                                                {opt.valueName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleSaveVitals}
                                        disabled={isSavingVitals || !effectiveResidentId}
                                        sx={{
                                            bgcolor: "#48CAE4",
                                            color: "#000000",
                                            fontWeight: "normal",
                                            "&:hover": { bgcolor: "#3ab3cc" },
                                            "&.Mui-disabled": {
                                                bgcolor: "rgba(0, 0, 0, 0.12)",
                                                color: "rgba(0, 0, 0, 0.26)"
                                            }
                                        }}
                                    >
                                        {isSavingVitals ? (
                                            <CircularProgress size={16} />
                                        ) : editingVitalId ? (
                                            "Update"
                                        ) : (
                                            "Add"
                                        )}
                                    </Button>
                                    {editingVitalId && (
                                        <Button variant="outlined" size="small" onClick={cancelEditVital}>
                                            Cancel
                                        </Button>
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>

                        {vitalsHistory.map((vital: any, index: number) => (
                            <TableRow key={vital.id || index}>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                    {vital.vitalDate
                                        ? formatToUIDateTimeLong(vital.vitalDate)
                                        : "-"}
                                </TableCell>
                                <TableCell>{vital.dailyWeight || vital.weight || "-"}</TableCell>
                                <TableCell>{vital.temperature || "-"}</TableCell>
                                <TableCell>{vital.pulseRate || "-"}</TableCell>
                                <TableCell>{vital.dailyBloodPressure || vital.bloodPressure || "-"}</TableCell>
                                <TableCell>{vital.respirationRate || "-"}</TableCell>
                                <TableCell>{vital.dailyBloodSugar || vital.bloodSugar || "-"}</TableCell>
                                <TableCell>{vital.spO2 || vital.oxygenSaturation || "-"}</TableCell>
                                <TableCell>{vital.painLevel || "-"}</TableCell>
                                <TableCell>{vital.levelOfConsciousness || vital.consciousnessLevel || "-"}</TableCell>
                                <TableCell>{vital.mealPortion || "-"}</TableCell>
                                <TableCell>{vital.bowelMovement || "-"}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        {vital.vitalDate && isSameDay(new Date(vital.vitalDate), new Date()) && (
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleEditVital?.(vital)}
                                                disabled={editingVitalId === vital.id}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                        label="Date & Time"
                        format="dd/MM/yyyy hh:mm a"
                        value={todaysVitals.vitalDate}
                        maxDate={new Date()}
                        readOnly={true}
                        minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                        onChange={(newValue) =>
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                vitalDate: newValue,
                            }))
                        }
                        slotProps={{
                            textField: {
                                fullWidth: true,
                            },
                        }}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Weight"
                    fullWidth
                    value={todaysVitals.weight}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d{0,5}(\.\d{0,5})?$/.test(val)) {
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                weight: val,
                            }));
                        }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Temperature"
                    fullWidth
                    value={todaysVitals.temperature}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d{0,5}(\.\d{0,5})?$/.test(val)) {
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                temperature: val,
                            }));
                        }
                    }}
                    error={!!vitalsError && !todaysVitals.temperature}
                    helperText={
                        !!vitalsError && !todaysVitals.temperature
                            ? "This field is mandatory."
                            : ""
                    }
                    required
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Pulse Rate"
                    fullWidth
                    value={todaysVitals.pulseRate}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d{0,5}$/.test(val)) {
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                pulseRate: val,
                            }));
                        }
                    }}
                    error={!!vitalsError && !todaysVitals.pulseRate}
                    helperText={
                        !!vitalsError && !todaysVitals.pulseRate
                            ? "This field is mandatory."
                            : ""
                    }
                    required
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Blood Pressure"
                    placeholder="120/80"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={todaysVitals.bloodPressure}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^[0-9/]*$/.test(val) && val.length <= 7) {
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                bloodPressure: val,
                            }));
                        }
                    }}
                    helperText={
                        todaysVitals.bloodPressure &&
                            !/^\d{2,3}\/\d{2,3}$/.test(todaysVitals.bloodPressure)
                            ? "Format: 120/80"
                            : ""
                    }
                    error={
                        todaysVitals.bloodPressure !== "" &&
                        !/^\d{2,3}\/\d{2,3}$/.test(todaysVitals.bloodPressure)
                    }
                    sx={{
                        "& .MuiInputBase-input::placeholder": {
                            color: "#9CA3AF !important",
                            WebkitTextFillColor: "#9CA3AF !important",
                            opacity: "1 !important"
                        }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Respiration Rate"
                    fullWidth
                    value={todaysVitals.respirationRate}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d{0,5}$/.test(val)) {
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                respirationRate: val,
                            }));
                        }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Blood Sugar"
                    fullWidth
                    value={todaysVitals.bloodSugar}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d{0,5}(\.\d{0,5})?$/.test(val)) {
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                bloodSugar: val,
                            }));
                        }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="O2 Saturation"
                    fullWidth
                    value={todaysVitals.oxygenSaturation}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d{0,5}(\.\d{0,5})?$/.test(val)) {
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                oxygenSaturation: val,
                            }));
                        }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Pain Level"
                    fullWidth
                    value={todaysVitals.painLevel}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val) && !/^0\d/.test(val)) {
                            if (val === "" || (Number(val) >= 0 && Number(val) <= 10)) {
                                setTodaysVitals((prev: any) => ({
                                    ...prev,
                                    painLevel: val,
                                }));
                            }
                        }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Level of Consciousness"
                    fullWidth
                    value={todaysVitals.consciousnessLevel}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^[a-zA-Z0-9\s]*$/.test(val) && val.length <= 5) {
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                consciousnessLevel: val,
                            }));
                        }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Meal Portion Consumed"
                    fullWidth
                    value={todaysVitals.mealPortionConsumed}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val) && !/^0\d/.test(val)) {
                            if (val === "" || (Number(val) >= 0 && Number(val) <= 100)) {
                                setTodaysVitals((prev: any) => ({
                                    ...prev,
                                    mealPortionConsumed: val,
                                }));
                            }
                        }
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                    <InputLabel>Bowel Movement</InputLabel>
                    <Select
                        label="Bowel Movement"
                        value={todaysVitals.bowelMovement}
                        onChange={(e) =>
                            setTodaysVitals((prev: any) => ({
                                ...prev,
                                bowelMovement: e.target.value,
                            }))
                        }
                        displayEmpty
                    >
                        <MenuItem value="" disabled>Select</MenuItem>
                        {bowelMovementOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.valueName}>
                                {opt.valueName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                        mt: 1,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleSaveVitals}
                        disabled={isSavingVitals || !effectiveResidentId}
                        sx={{
                            minWidth: 120,
                            bgcolor: "#48CAE4",
                            color: "#000000",
                            fontWeight: "normal",
                            "&:hover": { bgcolor: "#3ab3cc" },
                            "&.Mui-disabled": {
                                bgcolor: "rgba(0, 0, 0, 0.12)",
                                color: "rgba(0, 0, 0, 0.26)"
                            }
                        }}
                    >
                        {isSavingVitals ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Saving...
                            </>
                        ) : editingVitalId ? (
                            "Update"
                        ) : (
                            "Add"
                        )}
                    </Button>
                    {editingVitalId && (
                        <Button variant="outlined" onClick={cancelEditVital}>
                            Cancel
                        </Button>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default VitalsForm;